import {Collapse, css, useTheme} from '@mui/material';
import {GetStylesFunction, IconHeaderWithSearch, MaximizeButton} from '@wallaby-ui/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {LightFilterTabDef, LightFilterTabs} from './LightFilterTabs';
import {useFormatters} from './LightFormatter';
import {LightSavedFiltersBox, LightSavedFiltersHeader} from './LightSavedFiltersBox';

/**
 * Saved-filters wiring passed through to LightSavedFiltersBox — everything except the props the
 * panel drives itself (open/toggle/hasActiveFilter) and the layout variants it hard-codes.
 */
export type LightFilterPanelSavedFiltersProps = Omit<
  React.ComponentProps<typeof LightSavedFiltersBox>,
  | 'open'
  | 'toggleOpen'
  | 'hasActiveFilter'
  | 'detachedHeader'
  | 'fitToFilters'
  | 'isPopupMode'
  | 'limitHeight'
  | 'savedFiltersScrollBoxStyles'
>;

/**
 * Tabbed filter panel (PDM-2750): the strip sits on the "Filters" header line. `panels` holds the inputs of every
 * tab; ALL of them stay mounted and the inactive ones are only hidden, so switching a tab does not re-create the
 * heavy filter fields (ATC tree, organizations) and stays as cheap as it was before the tabs existed.
 */
export type LightFilterPanelTabsProps = {
  tabs: LightFilterTabDef[];
  activeTab: string;
  /**
   * `open` is true when the tab was selected on a collapsed panel: the page must open the panel in the SAME state
   * update. Two separate updates (tab, then toggleOpen) would each merge into the state of the last render, so
   * the second one would restore the previous tab (useLocationQuery merges that way).
   */
  onChange: (tabId: string, open: boolean) => void;
  panels: Record<string, React.ReactNode>;
};

type Props = {
  /** Filters-open flag (drives both Collapse sections, the saved-filters content and the toggle icon). */
  open: boolean;
  toggleOpen: () => void;
  /** Highlights the toggle icon when the panel is collapsed but filters are active. */
  hasActiveFilter?: boolean;
  /** Extra content for the "Filters" header (e.g. a reset-filters chip on report pages). */
  filtersHeaderChildren?: React.ReactNode;
  /**
   * Splits the filter inputs into tabs: the tab strip is rendered right after the "Filters" title and stays
   * visible even when the panel is collapsed (its badges still tell where the active filters are). Selecting
   * a tab on a collapsed panel opens it (through `tabs.onChange`, see there). The inputs come from `tabs.panels`;
   * `children` is not rendered then.
   */
  tabs?: LightFilterPanelTabsProps;
  savedFiltersProps: LightFilterPanelSavedFiltersProps;
  /**
   * Active-filter chips content (typically LightSelectedFiltersPane) rendered inside the LEFT column
   * directly below the filter inputs — it fills the whitespace the taller saved-filters column leaves
   * there and never runs under that column; pass null/undefined to render no chips section.
   */
  selectedFilters?: React.ReactNode;
  /**
   * Number of chips in `selectedFilters`. When given, the chips get an "Active filters" heading with this
   * count in a badge (the tabbed panel's always-visible active-filters section).
   */
  selectedFiltersCount?: number;
  /**
   * Fullscreen toggle for the WHOLE panel (PDM-2842): adds a maximize button to the "Saved filters" header. The
   * page owns the useMaximizeWrapper (its MaximizeWrapper replaces the panel's card, the way the results table
   * does it) and passes the state here; the panel keeps it consistent with `open` - maximizing a collapsed
   * panel expands it, and hiding the filters while maximized also restores the normal size (a collapsed
   * fullscreen panel would be an empty page).
   */
  maximize?: {maximized: boolean; setMaximized: (maximized: boolean) => void};
  /**
   * Height cap for the scroll area — for pages WITHOUT a split pane (full-expand report pages),
   * where nothing else bounds the panel and a long saved-filters list would grow the card without
   * limit. Use a viewport-relative unit (e.g. '40vh') so data below the panel stays visible even on
   * small screens; past the cap the area's single scrollbar appears. Split pages omit this — the
   * pane bounds the panel there.
   */
  maxScrollAreaHeight?: string;
  /** Ref to the single scrollable area (e.g. for scroll-to-element effects). */
  scrollAreaRef?: React.Ref<HTMLDivElement>;
  /**
   * Ref to the filter-inputs wrapper, for LightSplitLayout.autoFitFilterRows' contentRef. The wrapper
   * also carries the `data-filter-inputs` marker, so the ref can be omitted and autoFitFilterRows used
   * without contentRef.
   */
  filterInputsRef?: React.Ref<HTMLDivElement>;
  /**
   * Width in px the LEFT column keeps for its inputs before the saved-filters column starts to shrink -
   * for pages with a multi-field row (a lab method + operator + value) that must not wrap on a normal
   * screen. Capped at the panel width, so a narrow window still wraps the rows instead of clipping them.
   */
  filterInputsMinWidth?: number;
  /** The page's filter inputs (ignored with `tabs`, which brings its own panels). */
  children?: React.ReactNode;
};

/**
 * The merged filter panel (PDW-155): a fixed header row ("Filters" left, "Saved filters" + toggle
 * right) above ONE shared scroll area holding the two-column row — filter inputs left, saved-filters
 * content right. The active-filter chips (PDW-274) live at the bottom of the LEFT column, right under
 * the last filter row, so they use the whitespace the taller saved-filters column leaves there instead
 * of a full-width band under both columns. Chips past the fold are reached by the single scrollbar.
 *
 * With `tabs` (PDM-2750) the inputs are one tab panel at a time; the saved filters column and the chips
 * section do not depend on the tab, so they stay where they are whichever tab is selected.
 *
 * The root div owns the header→content spacing (none — matching the original stacked header+scroll
 * box, where the visible whitespace comes from the inputs' floating labels) so the parent card's
 * row-gap applies only around the panel as a whole.
 */
export const LightFilterPanel: React.FC<Props> = (props) => {
  const {
    open,
    toggleOpen,
    hasActiveFilter,
    filtersHeaderChildren,
    tabs,
    savedFiltersProps,
    selectedFilters,
    selectedFiltersCount,
    maximize,
    maxScrollAreaHeight,
    scrollAreaRef,
    filterInputsRef,
    filterInputsMinWidth,
    children,
  } = props;
  const theme = useTheme();
  const {t} = useFormatters();
  const muiStyles = getStyles({theme});
  // The header row spans the full panel width while the columns live inside the scroll area — when
  // its scrollbar appears it squeezes the columns left, so the right-pinned saved-filters column no
  // longer lines up with its header. Mirror the scrollbar width as header-row right padding.
  const scrollAreaInnerRef = useRef<HTMLDivElement | null>(null);
  const [scrollAreaScrollbarWidth, setScrollAreaScrollbarWidth] = useState(0);
  const handleScrollAreaRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollAreaInnerRef.current = node;
      if (typeof scrollAreaRef === 'function') scrollAreaRef(node);
      else if (scrollAreaRef) (scrollAreaRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [scrollAreaRef]
  );
  useEffect(() => {
    const node = scrollAreaInnerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const updateScrollbarWidth = () => setScrollAreaScrollbarWidth(node.offsetWidth - node.clientWidth);
    updateScrollbarWidth();
    const observer = new ResizeObserver(updateScrollbarWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  // Fullscreen panel (see `maximize`): the two flags stay consistent - the maximize button on a collapsed panel
  // also expands it, and the filter toggle (hide filters) on a maximized panel also leaves the fullscreen mode.
  const maximized = Boolean(maximize?.maximized);
  const toggleMaximized = () => {
    if (!maximized && !open) toggleOpen();
    maximize?.setMaximized(!maximized);
  };
  const toggleOpenRestoringSize = () => {
    if (maximized) maximize?.setMaximized(false);
    toggleOpen();
  };
  const savedFiltersHeaderButtons = maximize ? <MaximizeButton maximized={maximized} toggleMaximized={toggleMaximized} /> : undefined;
  const selectTab = (tabId: string) => tabs?.onChange(tabId, !open);
  return (
    // Cards have no bottom padding (content scrolls to their edge) — when the panel is collapsed
    // there is no content below the headers, so add the missing 16px to mirror the card's top
    // padding and keep the header row vertically centered in the collapsed bar.
    <div css={muiStyles.panelRoot} style={!open ? {paddingBottom: 16} : undefined}>
      <div css={muiStyles.headerRow} style={scrollAreaScrollbarWidth ? {paddingRight: scrollAreaScrollbarWidth} : undefined}>
        <div css={muiStyles.headerLeft}>
          <IconHeaderWithSearch
            iconName={'filterIconMultiColor'}
            label={'agGrid.filters'}
            useFixedViewBox='0 0 20 16'
            iconCss={muiStyles.headerIcon}
            style={tabs ? {width: 'auto', flex: '0 0 auto'} : undefined}
          >
            {filtersHeaderChildren}
          </IconHeaderWithSearch>
          {tabs && <LightFilterTabs tabs={tabs.tabs} activeTab={tabs.activeTab} onChange={selectTab} />}
        </div>
        <div css={muiStyles.headerRight}>
          <LightSavedFiltersHeader open={open} toggleOpen={toggleOpenRestoringSize} hasActiveFilter={hasActiveFilter}>
            {savedFiltersHeaderButtons}
          </LightSavedFiltersHeader>
        </div>
      </div>
      <div ref={handleScrollAreaRef} css={muiStyles.scrollArea} style={maxScrollAreaHeight ? {maxHeight: maxScrollAreaHeight} : undefined}>
        <div css={muiStyles.columnsRow}>
          <div
            data-name='filters-wrapper'
            css={muiStyles.filtersBox}
            style={filterInputsMinWidth ? {minWidth: `min(${filterInputsMinWidth}px, 100%)`} : undefined}
          >
            <Collapse style={{width: '100%', minHeight: 0}} in={open} timeout={500}>
              {tabs ? (
                // One wrapper per tab, all mounted; only the visible one is the measured `data-filter-inputs` row.
                tabs.tabs.map((tab) => {
                  const isActive = tab.id === tabs.activeTab;
                  return (
                    <div
                      key={tab.id}
                      ref={isActive ? filterInputsRef : undefined}
                      data-filter-inputs={isActive ? '' : undefined}
                      data-filter-tab={tab.id}
                      css={muiStyles.filterInputsWrapper}
                      style={isActive ? {marginTop: 24} : {display: 'none'}}
                    >
                      {tabs.panels[tab.id]}
                    </div>
                  );
                })
              ) : (
                <div ref={filterInputsRef} data-filter-inputs css={muiStyles.filterInputsWrapper}>
                  {children}
                </div>
              )}
            </Collapse>
            {selectedFilters != null && (
              <Collapse style={{width: '100%', minHeight: 0}} in={open} timeout={500}>
                <div
                  data-name='selected-filters-wrapper'
                  css={muiStyles.selectedFiltersPaneWrapper}
                  // The tabbed layout gives the always-visible chips section more air above it (design: 44px).
                  style={tabs ? {marginTop: 32} : undefined}
                >
                  {selectedFiltersCount != null && (
                    <div css={muiStyles.selectedFiltersHeading} data-name='selected-filters-heading'>
                      <span css={muiStyles.selectedFiltersHeadingText}>{t('filter.activeFilters')}</span>
                      <span css={muiStyles.selectedFiltersBadge} data-name='selected-filters-count'>
                        {selectedFiltersCount}
                      </span>
                    </div>
                  )}
                  {selectedFilters}
                </div>
              </Collapse>
            )}
          </div>
          <LightSavedFiltersBox
            {...savedFiltersProps}
            open={open}
            toggleOpen={toggleOpenRestoringSize}
            hasActiveFilter={hasActiveFilter}
            detachedHeader
          />
        </div>
      </div>
    </div>
  );
};
LightFilterPanel.displayName = 'LightFilterPanel';

const getStyles: GetStylesFunction = (_options) => ({
  panelRoot: css`
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `,
  headerRow: css`
    width: 100%;
    display: flex;
    gap: 64px;
    align-items: center;
  `,
  // Title (auto width) + the tab strip (the rest of the line, wrapping its pills when squeezed).
  headerLeft: css`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    column-gap: 40px;
  `,
  // Mirrors the saved-filters column geometry so the header sits above its column.
  headerRight: css`
    flex: 1 1 0;
    max-width: 292px;
    width: 100%;
  `,
  headerIcon: css`
    width: 16px;
    height: 16px;
  `,
  // No row-gap here: a gap would keep 16px of empty space below the columns even when the panel is
  // collapsed (the chips Collapse still exists at 0 height). The chips wrapper carries its own
  // margin instead, which disappears inside the collapsed Collapse.
  scrollArea: css`
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    /* overflow-y: auto alone makes overflow-x compute to auto as well, so any transient
       few-px horizontal overflow (Collapse animation, sub-pixel column rounding) flashes a
       horizontal scrollbar strip under the section headers. Never scroll horizontally here. */
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
  `,
  columnsRow: css`
    width: 100%;
    display: flex;
    gap: 64px;
    align-items: stretch;
  `,
  filtersBox: css`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  `,
  filterInputsWrapper: css`
    display: flex;
    align-items: flex-start;
    column-gap: 8px;
    flex-wrap: wrap;
  `,
  // Top margin sits a touch above the gap between two filter rows (the ~13px of whitespace the
  // filters' 53px-high wrappers leave around their ~40px inputs), so the chips read as a separate
  // block below the inputs rather than as one more filter row.
  selectedFiltersPaneWrapper: css`
    margin: 16px 0 16px 0;
  `,
  selectedFiltersHeading: css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  `,
  selectedFiltersHeadingText: css`
    font-family: SourceSansPro-Semibold;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--dlThemeGray-500);
  `,
  selectedFiltersBadge: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    box-sizing: border-box;
    border-radius: 9px;
    font-family: SourceSansPro-Semibold;
    font-size: 11px;
    line-height: 18px;
    background: var(--dlTheme-50);
    color: var(--dlTheme-700);
  `,
});
